/**
 * applyFieldEditability — fold the server's per-caller `editable_for_me` into
 * the `editable` flag the library's form filter already understands.
 *
 * `GET /metadata` returns `editable_for_me` for the tables that carry column
 * rules: `deals` (a full per-column matrix) and `lease_deeds` / `wells` (their
 * reserved columns — `lease_integrated_date` owned by BD.LeaseRecords,
 * `well_integrated_date` by BD.DivisionOrders, and the internal-only
 * `land_system_lease_id` / `wegid` / `allocated_value` by the land team, which
 * is what keeps an external brokerage from editing them).
 *
 * WigMetadataForm's default field filter is `!hide && editable !== false`, so
 * flipping `editable` to false is enough to drop a column from the edit form
 * while leaving it in the grid — i.e. it renders READ-ONLY rather than as an
 * input whose PUT comes back 403. The grid columns are built separately
 * (`metadataToAntdColumns`) and don't consult `editable`, so the value stays
 * visible; the user simply can't change it.
 *
 * Rows without `editable_for_me` (any table with no column rules, or an older
 * de-hub) pass through untouched.
 *
 * @param {Array<object>} metadata — UIMetadata rows for one table
 * @returns {Array<object>} the same rows, with `editable` narrowed
 */
export const applyFieldEditability = (metadata) =>
  (Array.isArray(metadata) ? metadata : []).map((m) =>
    m && m.editable_for_me === false ? { ...m, editable: false } : m
  );

/**
 * stripNonWritableFields — drop the columns THIS caller may not write from a
 * body about to be POSTed / PUT, judged by the same `editable_for_me` flag
 * `applyFieldEditability` folds into `editable`.
 *
 * Needed because the reserved columns render DISABLED rather than being
 * filtered out (`disableNonEditable` on WigMetadataForm /
 * WigMetadataCrudPanel — filtering them left BD.LeaseRecords staring at a
 * drawer holding one date picker and nothing identifying the lease). A
 * disabled input is still a registered form field, and the library's
 * `fromFormValues` maps an empty `Date` / `Timestamp` column to an explicit
 * `null` — so the body carries `lease_integrated_date: null` even though the
 * user never touched it.
 *
 * `POST /lease_deeds` and `POST /wells` judge every key the body CONTAINS
 * (`request.dict(exclude_unset=True)`) with no "did the value actually change"
 * narrowing — the PUTs get that from `changed_field_names`, the POSTs can't,
 * there being no stored row to diff against. So that echoed `null` reads as an
 * attempt to write a reserved column and the create 403s with "Your role is
 * not permitted to change the following field(s): lease_integrated_date" —
 * naming the one field the form had greyed out. Same story for
 * `well_integrated_date` on wells. The other reserved columns
 * (`land_system_lease_id`, `wegid`, `allocated_value`) escaped it only because
 * a non-date empty serializes to `undefined`, which `JSON.stringify` drops.
 *
 * Dropping the key is the right shape both ways: on create the column takes
 * its default anyway, and on update an omitted column is `exclude_unset` and
 * so not a write. Same move PaymentsTab makes with FULFILLMENT_FIELDS — that
 * table publishes no `editable_for_me`, so it hardcodes the list instead.
 *
 * Rows without `editable_for_me` (a table with no column rules, or a de-hub
 * predating the flag) are kept — fail-open, matching `applyFieldEditability`.
 *
 * @param {Record<string, unknown>} body — the serialized form body
 * @param {Array<object>} metadata — UIMetadata rows for the table (raw or
 *        already run through `applyFieldEditability`; the flag survives both)
 * @returns {Record<string, unknown>} the body without role-gated columns
 */
export const stripNonWritableFields = (body, metadata) => {
  const gated = new Set(
    (Array.isArray(metadata) ? metadata : [])
      .filter((m) => m && m.editable_for_me === false)
      .map((m) => m.field)
  );
  const out = {};
  for (const [k, v] of Object.entries(body || {})) {
    if (!gated.has(k)) out[k] = v;
  }
  return out;
};

export default applyFieldEditability;
